/**
 * DiscoverPage - Browse and follow stocks using Simulacra design system
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { YStack, XStack, H1, Text, Input, Spinner } from 'tamagui';
import { GlassCard, MatrixButton, UpgradeModal } from '../components/base';

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

interface Ticker {
  id: string;
  type: string;
  tags: {
    symbol: string;
    sector?: string;
    popularity?: number;
  };
  details: {
    company_name?: string;
    sector?: string;
    industry?: string;
  };
}

interface DiscoverPageProps {
  accessToken: string | null;
}

const DiscoverPage: React.FC<DiscoverPageProps> = ({ accessToken }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or defaults
  const [tickers, setTickers] = useState<Ticker[]>([]);
  const [followedTickers, setFollowedTickers] = useState<Map<string, string>>(new Map()); // symbol -> follow_id
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedSector, setSelectedSector] = useState(searchParams.get('sector') || 'All');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get('pageSize') || '20'));
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [followingTicker, setFollowingTicker] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (selectedSector && selectedSector !== 'All') {
      params.set('sector', selectedSector);
    }

    if (currentPage !== 1) {
      params.set('page', currentPage.toString());
    }

    if (pageSize !== 20) {
      params.set('pageSize', pageSize.toString());
    }

    setSearchParams(params, { replace: true });
  }, [selectedSector, currentPage, pageSize, setSearchParams]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchFollowedTickers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTickers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, selectedSector, debouncedSearchQuery]);

  const fetchTickers = async () => {
    setLoading(true);
    setError('');

    if (!accessToken) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      // Build query params
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
      });

      // Add search query if present (using debounced value)
      if (debouncedSearchQuery.trim()) {
        params.append('search', debouncedSearchQuery.trim());
      }

      // Add sector filter if not "All"
      if (selectedSector !== 'All') {
        params.append('tags.sector', selectedSector);
      }

      // Use the new pagination endpoint
      const response = await fetch(`${API_URL}/tickers/query?${params}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Protocol Beamer response format
        setTickers(data.data || []);
        setTotalItems(data.pagination?.total_items || 0);
        setTotalPages(data.pagination?.total_pages || 0);
        setHasNextPage(data.pagination?.has_next_page || false);
        setHasPreviousPage(data.pagination?.has_previous_page || false);
      } else {
        setError('Failed to load tickers');
      }
    } catch (err) {
      setError('Failed to connect to the API');
    } finally {
      setLoading(false);
    }
  };

  const fetchFollowedTickers = async () => {
    if (!accessToken) return;

    try {
      const response = await fetch(`${API_URL}/ticker_follow/query`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const followed = new Map<string, string>();
        (data.tickers || []).forEach((ft: any) => {
          const symbol = ft.ticker_details?.tags?.symbol;
          const followId = ft.id;
          if (symbol && followId) {
            followed.set(symbol, followId);
          }
        });
        setFollowedTickers(followed);
      }
    } catch (error) {
      console.error('Failed to fetch followed tickers:', error);
    }
  };

  const handleFollow = async (ticker: Ticker, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!accessToken) {
      navigate('/login');
      return;
    }

    setFollowingTicker(ticker.tags.symbol);

    try {
      const companyName = ticker.details.company_name || ticker.tags.symbol;
      const sector = ticker.tags.sector || ticker.details.sector || '';
      const response = await fetch(
        `${API_URL}/ticker_follow/create?ticker=${ticker.tags.symbol}&company_name=${encodeURIComponent(companyName)}&sector=${encodeURIComponent(sector)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const followId = data.entity.id;
        setFollowedTickers(prev => {
          const newMap = new Map(prev);
          newMap.set(ticker.tags.symbol, followId);
          return newMap;
        });
      } else if (response.status === 403) {
        // Tier limit reached - show upgrade modal
        const data = await response.json();
        if (data.detail === 'upgrade_required') {
          setShowUpgradeModal(true);
        } else {
          alert(data.detail || 'Access denied');
        }
      } else {
        const data = await response.json();
        alert(data.detail || data.error || 'Failed to follow ticker');
      }
    } catch (error) {
      console.error('Failed to follow ticker:', error);
      alert('Failed to follow ticker');
    } finally {
      setFollowingTicker(null);
    }
  };

  const handleUnfollow = async (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!accessToken) return;

    const followId = followedTickers.get(symbol);
    if (!followId) return;

    setFollowingTicker(symbol);

    try {
      const response = await fetch(
        `${API_URL}/ticker_follow/delete/${followId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setFollowedTickers(prev => {
          const newMap = new Map(prev);
          newMap.delete(symbol);
          return newMap;
        });
      } else {
        alert('Failed to unfollow ticker');
      }
    } catch (error) {
      console.error('Failed to unfollow ticker:', error);
      alert('Failed to unfollow ticker');
    } finally {
      setFollowingTicker(null);
    }
  };

  const handleSectorChange = (sector: string) => {
    setSelectedSector(sector);
    setCurrentPage(1); // Reset to page 1 when filter changes
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to page 1 when search changes
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to page 1 when page size changes
  };

  const getSectors = () => {
    // Hardcoded common sectors - we can fetch these from /tickers/sectors endpoint later
    return ['All', 'Technology', 'Healthcare', 'Financial Services', 'Consumer Cyclical',
            'Industrials', 'Communication Services', 'Energy', 'Consumer Defensive',
            'Real Estate', 'Utilities', 'Basic Materials'];
  };

  const handleTickerClick = (symbol: string) => {
    navigate(`/chat?ticker=${symbol}`);
  };

  return (
    <YStack flex={1} padding="$4" gap="$6" maxWidth={1400} marginHorizontal="auto" width="100%">
      {/* Header */}
      <YStack gap="$2" alignItems="center" paddingVertical="$4">
        <H1 color="$green10" fontFamily="$heading" textAlign="center">
          Discover Stocks
        </H1>
        <Text color="$colorPress" fontFamily="$body" fontSize="$4" textAlign="center">
          Browse and follow stocks to chat with them
        </Text>
      </YStack>

      {/* Filters */}
      <GlassCard>
        <XStack gap="$3" flexWrap="wrap" $sm={{ flexDirection: 'column' }}>
          <Input
            flex={1}
            minWidth={250}
            placeholder="Search by symbol, company, or sector..."
            value={searchQuery}
            onChangeText={handleSearchChange}
            backgroundColor="$backgroundHover"
            borderColor="$borderColor"
            color="$color"
            placeholderTextColor="$placeholderColor"
            fontFamily="$body"
          />
          <select
            value={selectedSector}
            onChange={(e) => handleSectorChange(e.target.value)}
            style={{
              minWidth: '200px',
              padding: '12px 16px',
              backgroundColor: 'rgba(20, 20, 20, 0.6)',
              border: '1px solid rgba(125, 211, 192, 0.2)',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontFamily: 'Space Mono, monospace',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            {getSectors().map((sector) => (
              <option key={sector} value={sector}>
                {sector === 'All' ? 'All Sectors' : sector}
              </option>
            ))}
          </select>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            style={{
              minWidth: '120px',
              padding: '12px 16px',
              backgroundColor: 'rgba(20, 20, 20, 0.6)',
              border: '1px solid rgba(125, 211, 192, 0.2)',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontFamily: 'Space Mono, monospace',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </XStack>
      </GlassCard>

      {/* Loading state */}
      {loading && (
        <YStack alignItems="center" gap="$4" paddingVertical="$6">
          <Spinner size="large" color="$green10" />
          <Text color="$green10" fontFamily="$mono">
            Loading tickers...
          </Text>
        </YStack>
      )}

      {/* Error message */}
      {error && (
        <GlassCard backgroundColor="$backgroundPress" borderColor="$red10" borderWidth={2}>
          <Text color="$red10" fontFamily="$mono">
            {error}
          </Text>
        </GlassCard>
      )}

      {/* Results */}
      {!loading && !error && (
        <YStack gap="$4">
          {/* Results count and pagination info */}
          <XStack gap="$2" flexWrap="wrap" alignItems="center" justifyContent="center">
            <Text color="$colorPress" fontFamily="$mono" fontSize="$3">
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalItems)} of {totalItems} tickers
            </Text>
            {followedTickers.size > 0 && (
              <>
                <Text color="$borderColor" fontFamily="$mono" fontSize="$3">
                  •
                </Text>
                <Text color="$green10" fontFamily="$mono" fontSize="$3">
                  Following {followedTickers.size}
                </Text>
              </>
            )}
            <Text color="$borderColor" fontFamily="$mono" fontSize="$3">
              •
            </Text>
            <Text color="$colorPress" fontFamily="$mono" fontSize="$3">
              Page {currentPage} of {totalPages}
            </Text>
          </XStack>

          {/* Ticker Grid */}
          {tickers.length > 0 ? (
            <XStack flexWrap="wrap" gap="$3" justifyContent="center">
              {tickers.map((ticker) => {
                const symbol = ticker.tags.symbol;
                const isFollowing = followedTickers.has(symbol);
                const isProcessing = followingTicker === symbol;

                return (
                  <GlassCard
                    key={ticker.id}
                    glowOnHover={!isFollowing}
                    onPress={() => handleTickerClick(symbol)}
                    pressStyle={{ scale: 0.98 }}
                    cursor="pointer"
                    minWidth={280}
                    maxWidth={320}
                    flex={1}
                    backgroundColor={isFollowing ? '$backgroundHover' : '$backgroundPress'}
                    borderColor={isFollowing ? '$green10' : '$borderColor'}
                    borderWidth={isFollowing ? 2 : 1}
                    $sm={{ minWidth: 240, maxWidth: '100%' }}
                  >
                    <YStack gap="$3">
                      {/* Ticker info */}
                      <YStack gap="$2">
                        <Text
                          fontFamily="$heading"
                          fontSize="$6"
                          fontWeight="bold"
                          color="$green10"
                        >
                          {symbol}
                        </Text>
                        <Text
                          fontFamily="$body"
                          fontSize="$3"
                          color="$color"
                          numberOfLines={2}
                        >
                          {ticker.details.company_name || symbol}
                        </Text>
                        <Text
                          fontFamily="$mono"
                          fontSize="$2"
                          color="$placeholderColor"
                        >
                          {ticker.tags.sector || ticker.details.sector || 'Unknown'}
                        </Text>
                      </YStack>

                      {/* Follow button */}
                      <MatrixButton
                        variant={isFollowing ? 'secondary' : 'primary'}
                        size="$2"
                        onPress={(e: any) => isFollowing ? handleUnfollow(symbol, e) : handleFollow(ticker, e)}
                        disabled={isProcessing}
                      >
                        {isProcessing ? '...' : isFollowing ? '✓ Following' : '+ Follow'}
                      </MatrixButton>
                    </YStack>
                  </GlassCard>
                );
              })}
            </XStack>
          ) : (
            <GlassCard>
              <YStack gap="$2" alignItems="center" paddingVertical="$4">
                <Text fontFamily="$body" fontSize="$4" color="$color">
                  {searchQuery.trim() ? `No tickers found matching "${searchQuery}"` : 'No tickers found'}
                </Text>
              </YStack>
            </GlassCard>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <GlassCard>
              <XStack gap="$3" justifyContent="center" alignItems="center" flexWrap="wrap">
                <MatrixButton
                  variant="secondary"
                  size="$2"
                  onPress={() => handlePageChange(currentPage - 1)}
                  disabled={!hasPreviousPage}
                  opacity={hasPreviousPage ? 1 : 0.5}
                >
                  ← Previous
                </MatrixButton>

                <XStack gap="$2" alignItems="center">
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$3">
                    Page
                  </Text>
                  <select
                    value={currentPage}
                    onChange={(e) => handlePageChange(Number(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'rgba(20, 20, 20, 0.6)',
                      border: '1px solid rgba(125, 211, 192, 0.2)',
                      borderRadius: '8px',
                      color: '#e0e0e0',
                      fontFamily: 'Space Mono, monospace',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                  >
                    {Array.from({ length: Math.min(totalPages, 100) }, (_, i) => i + 1).map((page) => (
                      <option key={page} value={page}>
                        {page}
                      </option>
                    ))}
                  </select>
                  <Text color="$colorPress" fontFamily="$mono" fontSize="$3">
                    of {totalPages}
                  </Text>
                </XStack>

                <MatrixButton
                  variant="secondary"
                  size="$2"
                  onPress={() => handlePageChange(currentPage + 1)}
                  disabled={!hasNextPage}
                  opacity={hasNextPage ? 1 : 0.5}
                >
                  Next →
                </MatrixButton>
              </XStack>
            </GlassCard>
          )}
        </YStack>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={() => {
          setShowUpgradeModal(false);
          navigate('/subscription');
        }}
        feature="following tickers"
      />
    </YStack>
  );
};

export default DiscoverPage;
